/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {Alert, Breadcrumb, Card, Descriptions, Result, Tabs, Tooltip} from "antd";
import {Link, useParams} from "react-router-dom";
import {Person} from "../core/datamodel";
import {get} from "../core/request";
import {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../store";
import {ViewState} from "../states";
import {openDrawer} from "../reducers/drawers-reducer";
import {CustomButton} from "../components/buttons";
import {IconSet} from "../components/icons";
import Loading from "../components/loading";
import {ApiTable} from "../components/table";

export default function PersonDetailsView(props: any) {
    const params: { id: string } = useParams();
    const reloadSignal = useAppSelector(state => state.reloadSignals.person)
    const [person, setPerson] = useState({} as Person);
    const [state, setState] = useState(ViewState.loading);

    useEffect(() => {
        get("persons/" + params.id).done((response) => {
                setPerson(new Person(response.data.person));
                setState(ViewState.success);
            }
        ).fail(() => setState(ViewState.failed))
    }, [params.id, reloadSignal])

    let dispatch = useAppDispatch();

    const onEdit = () => dispatch(openDrawer({key: 'person', object: person}));


    const breadcrumb = (
        <Breadcrumb style={{paddingTop: 6, paddingBottom: 6}}>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/persons">Kontakte</Link></Breadcrumb.Item>
            <Breadcrumb.Item>{!!person?.props?.salutation ? person.props.salutation : "Unknown"}</Breadcrumb.Item>
        </Breadcrumb>
    )

    const pageHeader = (
        <Card
            title={person?.props?.salutation}
            extra={
                <CustomButton mode={'edit'} onClick={onEdit} tooltip={"Person bearbeiten"}/>
            }>
            <Descriptions colon={false} size="small">
                <Descriptions.Item
                    label={
                        <Tooltip overlay={"E-Mail"}>
                            <IconSet type='mail' style={{lineHeight: '24px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={
                        !!person?.props?.mail ?
                            (<a href={'Mailto:' + person.props.mail}>{person.props.mail}</a>) :
                            ("")
                    }
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Telefonnummer"}>
                            <IconSet type='phone' style={{lineHeight: '24px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={person?.props?.phone}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Mobilnummer"}>
                            <IconSet type='mobile' style={{lineHeight: '24px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={person?.props?.mobile}
                />
            </Descriptions>
        </Card>
    )

    const disabledAlert = (
        !!person?.props?.disabled ? (<Alert
            message="Dieser Kontakt ist deaktiviert!"
            description="Zur weiteren Verwendnung bearbeiten und reaktivieren."
            type="warning"
            showIcon={true}
            style={{marginBottom: 24}}
        />) : (<></>)
    )

    const tabs = (
        <Tabs
            defaultActiveKey={person?.props?.comment ? "1" : "2"}
            style={{minHeight: '100%'}}
            animated
            tabBarStyle={{paddingLeft: '12px', marginBottom: 12}}>
            <Tabs.TabPane
                tab={<><IconSet type='notes'/> Notizen</>}
                key="1"
                disabled={!person?.props?.comment}
            >
                <Card title={"Notizen"}>
                    {person?.props?.comment?.split("\n").map(p => <>{p}<br/></>)}
                </Card>
            </Tabs.TabPane>

            <Tabs.TabPane
                tab={<><IconSet type='devices'/> Geräte</>}
                key="2"
            >
                <ApiTable
                    title={<>Verwaltete Geräte</>}
                    id="person_device"
                    url={"persons/" + person?.props?.uuid + "/devices"}
                    reloadSignals={[
                        useAppSelector(state => state.reloadSignals.person),
                        useAppSelector(state => state.reloadSignals.device)
                    ]}
                    clickable={"/devices"}
                />
            </Tabs.TabPane>

            <Tabs.TabPane
                tab={<><IconSet type='customers'/> Kunden</>}
                key="3"
            >
                <ApiTable
                    title={<>Zugehörige Kunden</>}
                    id="person_customer"
                    url={"persons/" + person?.props?.uuid + "/customers"}
                    reloadSignals={[
                        useAppSelector(state => state.reloadSignals.person),
                        useAppSelector(state => state.reloadSignals.customer)
                    ]}
                    clickable={"/customers"}
                />
            </Tabs.TabPane>
        </Tabs>
    )

    switch (state) {
        case ViewState.loading:
            return (
                <>
                    {breadcrumb}
                    <Loading/>
                </>
            )
        case ViewState.success:
            return (
                <>
                    {breadcrumb}
                    {disabledAlert}
                    {pageHeader}
                    {tabs}
                </>
            )

        case ViewState.failed:
        default:
            return (
                <>
                    {breadcrumb}

                    <Result
                        status="404"
                        title="Nicht gefunden"
                        subTitle="Entschuldigung, die gesuchte Person konnte nicht gefunden werden!"
                    />
                </>
            )
    }
}